import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Sparkles, X, RefreshCcw, Lightbulb } from 'lucide-react';
import { FormValues, featureCategories } from '../FormSchema';
import { aiEnhanceDescription } from '@/utils/aiEnhanceService';

/**
 * Second step of the multi-step form - Features & Details
 */
export function FeaturesStep() {
  const { register, formState: { errors }, setValue, watch } = useFormContext<FormValues>();
  const selectedFeatures = watch('features') || [];
  const hasDesign = watch('has_design');
  const [isEnhancing, setIsEnhancing] = useState(false);
  // AI enhancement protection
  const [enhancementCount, setEnhancementCount] = useState(0);
  const MAX_ENHANCEMENTS = 5;
  
  // Sample description text for users to try
  const SAMPLE_DESCRIPTION = "I want to build a coffee bean discovery app that shows users locally grown coffee beans in their area. Users should be able to browse different varieties, see flavor profiles, and find local shops that sell these beans. The app needs a map feature, search functionality, and a way for users to save their favorites.";


  // Design references state
  const [designRefs, setDesignRefs] = useState<Array<{ url: string; description: string }>>([]);

  // Initialize designRefs when toggled on
  React.useEffect(() => {
    if (hasDesign && designRefs.length === 0) {
      setDesignRefs([{ url: '', description: '' }]);
      setValue('design_references', []);
    }
    if (!hasDesign) {
      setDesignRefs([]);
      setValue('design_references', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDesign]);

  // Add reference
  const addDesignRef = () => {
    setDesignRefs([...designRefs, { url: '', description: '' }]);
  };

  // Remove reference
  const removeDesignRef = (index: number) => {
    const updated = designRefs.filter((_, i) => i !== index);
    setDesignRefs(updated);
    setValue('design_references', updated.map(ref => ref.url));
  };

  // Update reference
  const updateDesignRef = (index: number, field: 'url' | 'description', value: string) => {
    const updated = [...designRefs];
    updated[index][field] = value;
    setDesignRefs(updated);
    setValue('design_references', updated.map(ref => ref.url));
  };

  const [enhanceError, setEnhanceError] = useState<string | null>(null);
  const [aiFeatures, setAiFeatures] = useState<{ name: string; description: string; impact: string }[]>([]);
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newFeatureDescription, setNewFeatureDescription] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Add a new custom feature
  const addFeature = () => {
    if (!newFeatureName.trim()) return;
    const newFeature = {
      name: newFeatureName,
      description: newFeatureDescription || `Custom feature: ${newFeatureName}`,
      impact: 'medium',
    };
    setAiFeatures([...aiFeatures, newFeature]);
    setValue('features', [...aiFeatures, newFeature].map(f => f.name));
    setNewFeatureName('');
    setNewFeatureDescription('');
    setShowAddForm(false);
  };

  // Delete a feature
  const deleteFeature = (idx: number) => {
    const updated = [...aiFeatures];
    updated.splice(idx, 1);
    setAiFeatures(updated);
    setValue('features', updated.map(f => f.name));
  };

  // Enhance all current features
  const enhanceAllFeatures = async () => {
    if (aiFeatures.length === 0) return;
    setIsEnhancing(true);
    setEnhanceError(null);
    try {
      // Send the list of feature names/descriptions for enhancement
      const featuresPayload = aiFeatures.map(f => ({ name: f.name, description: f.description }));
      // We'll use the project description as context if available
      const currentDescription = watch('project_description') || '';
      // Now we can send both the project description and our features for enhancement
      const result = await aiEnhanceDescription(currentDescription, featuresPayload);
      if (result.error) {
        setEnhanceError(result.error);
        return;
      }
      setAiFeatures(result.features);
      setValue('features', result.features.map(f => f.name));
    } catch (error) {
      setEnhanceError('Failed to enhance features.');
    } finally {
      setIsEnhancing(false);
    }
  };

  // Handle AI enhancement of project description
  const handleEnhanceDescription = async () => {
    // Rate limiting: max 5 enhancements per session
    if (enhancementCount >= MAX_ENHANCEMENTS) {
      setEnhanceError("Maximum enhancement limit reached for this session.");
      return;
    }
    // Form start time check: must wait at least 5 seconds
    const formStartTime = watch('form_start_time');
    const now = Date.now();
    if (formStartTime && now - formStartTime < 5000) {
      setEnhanceError("Please take a few seconds to fill out your description before enhancing.");
      return;
    }

    const currentDescription = watch('project_description');
    if (!currentDescription || currentDescription.trim().length < 10) {
      setEnhanceError("Please enter at least a brief description first (10+ characters)");
      return;
    }
    setIsEnhancing(true);
    setEnhanceError(null);
    try {
      const result = await aiEnhanceDescription(currentDescription);
      if (result.error) {
        setEnhanceError(result.error);
        return;
      }
      setValue('project_description', result.enhancedDescription);
      // Set AI features for display
      if (result.features && result.features.length > 0) {
        setAiFeatures(result.features);
        setValue('features', result.features.map(f => f.name));
      } else {
        setAiFeatures([]);
        setValue('features', []);
      }
      // Increment enhancement count after success
      setEnhancementCount(prev => prev + 1);
    } catch (error) {
      console.error("Enhancement failed:", error);
      setEnhanceError("Failed to enhance description. Please try again or continue with your original text.");
    } finally {
      setIsEnhancing(false);
    }
  };

  
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label htmlFor="project_description" className="text-white/90">
            Project Description <span className="text-red-400">*</span>
          </Label>
          <div className="flex gap-2">
            <Button 
              type="button" 
              size="sm"
              variant="secondary"
              className="text-xs bg-purple-800/50 text-white border-purple-500/30 hover:bg-purple-700/60"
              onClick={() => {
                setValue('project_description', SAMPLE_DESCRIPTION);
                setValue('features', []);
                setAiFeatures([]);
              }}
              title="Insert a sample description"
            >
              <Lightbulb className="w-3 h-3 mr-1" />
              Example
            </Button>
            <Button 
              type="button" 
              size="sm"
              variant="secondary"
              className="text-xs bg-gray-800/60 text-white border-gray-600/30 hover:bg-gray-700/70"
              onClick={() => {
                setValue('project_description', '');
                setValue('features', []);
                setAiFeatures([]);
                setEnhancementCount(0);
              }}
              title="Clear description and features"
            >
              <RefreshCcw className="w-3 h-3 mr-1" />
              Reset
            </Button>
            <Button 
              type="button" 
              size="sm"
              className="text-xs bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white border-none shadow-md hover:from-fuchsia-500 hover:to-purple-600 focus:ring-2 focus:ring-fuchsia-400"
              onClick={handleEnhanceDescription}
              disabled={isEnhancing}
            >
              <Sparkles className="w-3 h-3 mr-2" />
              {isEnhancing ? "Enhancing..." : "Enhance with AI"}
            </Button>
          </div>
        </div>
        <Textarea
          id="project_description"
          placeholder="Describe your project in detail. What problem does it solve? Who are the users? What are the key features you're looking for?"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/50 min-h-[120px]"
          {...register('project_description')}
        />
        {errors.project_description && (
          <p className="text-red-400 text-sm mt-1">{errors.project_description.message}</p>
        )}
        {enhanceError && (
          <p className="text-amber-400 text-sm mt-1">{enhanceError}</p>
        )}
        {isEnhancing && (
          <p className="text-blue-400 text-sm mt-1">Enhancing your description with AI...</p>
        )}
      </div>
      {/* Editable Key Features display */}
      {aiFeatures.length > 0 && (
        <div className="space-y-4 mt-8">
          <h3 className="text-lg font-medium text-white">Key Features</h3>
          <ul className="space-y-2">
            {aiFeatures.map((feature, idx) => (
              <li key={feature.name + idx} className="bg-white/5 border border-fuchsia-500/40 rounded-md p-3 text-white/90 flex justify-between items-start">
                <div>
                  <div className="font-semibold text-fuchsia-300">{feature.name}</div>
                  <div className="text-white/70 text-sm">{feature.description}</div>
                  <div className="text-xs text-fuchsia-400 mt-1">Impact: {feature.impact}</div>
                </div>
                <button
                  type="button"
                  className="ml-4 p-1 rounded hover:bg-fuchsia-900/30"
                  aria-label="Delete feature"
                  onClick={() => deleteFeature(idx)}
                >
                  <X className="w-4 h-4 text-fuchsia-300" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Add Feature Form */}
      <div className="mt-6">
        {!showAddForm ? (
          <Button
            type="button"
            size="sm"
            className="bg-fuchsia-700 text-white hover:bg-fuchsia-800"
            onClick={() => setShowAddForm(true)}
          >
            + Add Feature
          </Button>
        ) : (
          <div className="bg-white/5 border border-fuchsia-500/20 rounded-md p-4 mt-2 flex flex-col gap-2">
            <input
              type="text"
              className="px-2 py-1 rounded bg-black/30 border border-fuchsia-700 text-white"
              placeholder="Feature name"
              value={newFeatureName}
              onChange={e => setNewFeatureName(e.target.value)}
            />
            <textarea
              className="px-2 py-1 rounded bg-black/30 border border-fuchsia-700 text-white"
              placeholder="Feature description (optional)"
              value={newFeatureDescription}
              onChange={e => setNewFeatureDescription(e.target.value)}
            />
            <div className="flex gap-2 mt-2">
              <Button type="button" size="sm" className="bg-fuchsia-700 text-white" onClick={addFeature}>
                Add
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Enhance All Features Button */}
      {aiFeatures.length > 0 && (
        <div className="mt-4">
          <Button
            type="button"
            size="sm"
            className="bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white border-none shadow-md hover:from-fuchsia-500 hover:to-purple-600 focus:ring-2 focus:ring-fuchsia-400"
            onClick={enhanceAllFeatures}
            disabled={isEnhancing}
          >
            <Sparkles className="w-3 h-3 mr-2" />
            {isEnhancing ? "Enhancing Features..." : "Enhance My Features"}
          </Button>
          {enhanceError && (
            <p className="text-amber-400 text-sm mt-2">{enhanceError}</p>
          )}
        </div>
      )}
      
      <div className="space-y-2 mt-8 pb-2 border-t border-white/10 pt-6">
        <div className="flex items-center justify-between">
          <Label htmlFor="has_design" className="text-white/90">
            Do you have existing designs?
          </Label>
          <Switch
            id="has_design"
            checked={hasDesign}
            onCheckedChange={checked => setValue('has_design', checked)}
            className="data-[state=checked]:bg-fuchsia-600"
          />
        </div>
        <p className="text-xs text-white/60 mb-2">Let us know if you have mockups, wireframes, or any design assets</p>

        {/* Design References Dropdown */}
        {hasDesign && (
          <div className="mt-4 space-y-3 animate-fadeIn">
            <h4 className="text-md font-medium text-white/90">Design References</h4>
            <p className="text-sm text-white/70">Add links to your design mockups, wireframes, or inspiration websites</p>
            {designRefs.map((ref, idx) => (
              <div key={idx} className="flex flex-col gap-2 p-3 bg-white/5 rounded-md border border-fuchsia-500/20">
                <div className="flex justify-between items-center">
                  <label className="text-sm text-white/80">Reference #{idx + 1}</label>
                  <button
                    type="button"
                    onClick={() => removeDesignRef(idx)}
                    className="text-fuchsia-400 hover:text-fuchsia-300"
                  >
                    <X size={16} />
                  </button>
                </div>
                <input
                  type="url"
                  placeholder="URL (e.g., https://figma.com/...)" 
                  value={ref.url}
                  onChange={e => updateDesignRef(idx, 'url', e.target.value)}
                  className="px-3 py-2 bg-black/30 border border-white/10 rounded text-white"
                />
                <textarea
                  placeholder="Short description (optional)"
                  value={ref.description}
                  onChange={e => updateDesignRef(idx, 'description', e.target.value)}
                  className="px-3 py-2 bg-black/30 border border-white/10 rounded text-white min-h-[60px]"
                />
              </div>
            ))}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2 border-dashed border-fuchsia-500/40 text-fuchsia-400"
              onClick={addDesignRef}
            >
              + Add Another Reference
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
